/* eslint-disable camelcase */
import React, { ReactElement, useMemo } from "react";
import { Link } from "react-router";
import { EventLinkProps, EventLinkSubElementProps } from "./eventLink.model";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { getCategoryFromName } from "@companion-core/shared/app/Utils/bell";
import { capitalize } from "@companion-core/shared/app/Utils/format";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { getLang } from "@companion-core/web/src/Utilities/lang";

import "@companion-core/web/src/Assets/Styles/scss/eventLink.scss";

declare global {
  interface Optanon {
    ToggleInfoDisplay: () => void;
  }
  interface Window {
    Optanon: Optanon;
  }
}

const Internal = ({ path, onEventLinkClicked, children, name }: EventLinkSubElementProps) => {
  return (
    <Link
      data-testid={`event-link__internal--${name}`}
      to={path ?? ""}
      state={{ canGoBack: true }}
      className="event-link"
      onClick={() => onEventLinkClicked?.(name)}>
      {children}
    </Link>
  );
};

const Dynamic = ({ path, onEventLinkClicked, children, name }: EventLinkSubElementProps) => {
  return (
    <Link
      data-testid={`event-link__dynamic--${name}`}
      to={path ?? ""}
      state={{ canGoBack: true }}
      className="event-link"
      onClick={() => onEventLinkClicked?.(name)}>
      {children}
    </Link>
  );
};

const Cookies = ({ onEventLinkClicked, children, name }: EventLinkSubElementProps) => {
  const { publicConfig } = useConfigStore();

  return (
    <a
      data-testid={`event-link__cookies--${name}`}
      className="event-link"
      rel="noreferrer"
      href={
        publicConfig?.data?.cookie_banner_type === "digital-control-room"
          ? publicConfig?.data?.digital_control_room.link_urls[getLang()]
          : undefined
      }
      onClick={() => {
        publicConfig?.data?.cookie_banner_type === "one-trust" &&
          window.Optanon?.ToggleInfoDisplay();
        onEventLinkClicked && onEventLinkClicked(name);
      }}>
      {children}
    </a>
  );
};

const External = ({
  targetBlank,
  link,
  onEventLinkClicked,
  children,
  name,
}: EventLinkSubElementProps) => {
  return (
    <a
      data-testid={`event-link__external--${name}`}
      target={targetBlank ? "_blank" : "_self"}
      href={link}
      className="event-link"
      rel="noreferrer"
      onClick={() => onEventLinkClicked?.(name)}>
      {children}
    </a>
  );
};

const Concierge = ({ onEventLinkClicked, children, name }: EventLinkSubElementProps) => {
  const { setIsPopUpVisible, setBellContent, setBellCategory, setFormPath } = useBellStore(
    (state) => ({
      isPopUpVisible: state.isPopUpVisible,
      setIsPopUpVisible: state.setIsPopUpVisible,
      setBellContent: state.setBellContent,
      setBellCategory: state.setBellCategory,
      setFormPath: state.setFormPath,
    }),
  );

  return (
    <div
      data-testid={`event-link__concierge--${name}`}
      className="event-link"
      onClick={() => {
        const category = getCategoryFromName(name);
        if (category) {
          setFormPath(category.formPath);
          setBellContent("form");
          setBellCategory(category);
        } else {
          setBellContent("categories");
        }
        setIsPopUpVisible(true);
        onEventLinkClicked && onEventLinkClicked(name);
      }}>
      {children}
    </div>
  );
};

const Nav = ({ onEventLinkClicked, children, name }: EventLinkSubElementProps) => {
  return (
    <div
      data-testid={`event-link__nav--${name}`}
      className="event-link"
      onClick={() => onEventLinkClicked?.(name)}>
      {children}
    </div>
  );
};

const Logout = ({ onEventLinkClicked, children, name }: EventLinkSubElementProps) => {
  return (
    <div
      data-testid={`event-link__logout--${name}`}
      className="event-link"
      onClick={() => onEventLinkClicked?.(name)}>
      {children}
    </div>
  );
};

export const EventLink = ({
  name,
  type,
  path,
  link,
  targetBlank = true,
  children,
  onClick: onEventLinkClicked,
}: EventLinkProps) => {
  const capitalizedType: string = useMemo(() => capitalize(type ?? ""), [type]);

  const EventLinkComponents: Record<string, ReactElement> = {
    Internal: (
      <Internal onEventLinkClicked={onEventLinkClicked} path={path} name={name}>
        {children}
      </Internal>
    ),
    Dynamic: (
      <Dynamic onEventLinkClicked={onEventLinkClicked} path={path} name={name}>
        {children}
      </Dynamic>
    ),
    Cookies: (
      <Cookies name={name} onEventLinkClicked={onEventLinkClicked}>
        {children}
      </Cookies>
    ),
    External: (
      <External
        link={link}
        onEventLinkClicked={onEventLinkClicked}
        targetBlank={targetBlank}
        name={name}>
        {children}
      </External>
    ),
    Concierge: (
      <Concierge onEventLinkClicked={onEventLinkClicked} name={name}>
        {children}
      </Concierge>
    ),
    Nav: (
      <Nav onEventLinkClicked={onEventLinkClicked} name={name}>
        {children}
      </Nav>
    ),
    Logout: (
      <Logout onEventLinkClicked={onEventLinkClicked} name={name}>
        {children}
      </Logout>
    ),
  };

  if (!EventLinkComponents[capitalizedType]) return <></>;

  return EventLinkComponents[capitalizedType];
};
